import { useEffect, useState } from 'react';
import { FC } from 'react';
import { Card, Box, CardHeader, CircularProgress } from '@mui/material';
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State as ODataState } from '@progress/kendo-data-query';
import myCustomersApi from '../../api/MyCustomersApi';
import { CustomerShowAttendance } from 'src/types/customerShowAttendance';
import useAuth from '../../hooks/useAuth';
import TableHeader from '../TableHeader';
import ListIcon from '@mui/icons-material/List';
import DropdownLink from '../controls/DropdownLink';
import { useSearchParams } from 'react-router-dom';
import ShowOrderCell from '../grid/ShowOrderCell';
import FilterOperators from '../grid/FilterOperators';
import ColumnMenu from '../grid/ColumnMenu';
import useSettings from 'src/hooks/useSettings';
import { SETTING_KEYS } from 'src/constants';
import { getPageSizeSetting } from 'src/utils/settingsHelper';

const CustomerShows: FC<React.PropsWithChildren<unknown>> = () => {
  const CustomerShowPageSizeKey = SETTING_KEYS.CustomerShowPageSize;
  const { user } = useAuth();
  const [showAttendance, setShowAttendance] = useState<CustomerShowAttendance[]>([]);
  const [activeShows, setActiveShows] = useState<{ text: string; value: CompositeFilterDescriptor }[]>([]);
  const [selectedShow, setSelectedShow] = useState<number>(null);
  const [total, setTotal] = useState(0);
  const [search] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const savePagingSettings = useSettings((state) => state.savePagingSettings);
  const userSettings = useSettings((state) => state.userSettings);
  const pageSize = getPageSizeSetting(userSettings, CustomerShowPageSizeKey, 50);
  const [defaultFilter] = useState<CompositeFilterDescriptor | FilterDescriptor>({
    filters: [
      { field: 'salesRepId', value: user.salesReps[0], operator: 'eq' },
      { field: 'lawnAndGardenSalesRepId', value: user.salesReps[0], operator: 'eq' },
      { field: 'mcSalesRepId', value: user.salesReps[0], operator: 'eq' },
      { field: 'plantSalesRepId', value: user.salesReps[0], operator: 'eq' },
    ],
    logic: 'or',
  });
  const [gridDataState, setGridDataState] = useState<ODataState>({
    filter: {
      filters: [defaultFilter],
      logic: 'and',
    },
    skip: 0,
    take: pageSize,
    sort: [{ field: 'customerName', dir: 'asc' }],
  });

  const loadActiveShows = async () => {
    setIsLoading(true);
    myCustomersApi
      .getAllActiveShows()
      .then((response) => {
        let showFilters: { text: string; value: CompositeFilterDescriptor }[] = [];
        for (var i = 0; i <= response.length - 1; i++) {
          let newFilter: { text: string; value: CompositeFilterDescriptor };
          newFilter = { text: response[i].title, value: { logic: 'and', filters: [{ filters: [{ field: 'showId', value: response[i].value, operator: 'eq' }], logic: 'or' }] } };

          showFilters.push(newFilter);
        }
        setActiveShows(showFilters);
        setIsLoading(false);
      })
      .catch(() => {
        setActiveShows([]);
        setIsLoading(false);
      });
  };

  const loadShowAttendance = async (newGridDataState: ODataState) => {
    if (activeShows.length > 0) {
      myCustomersApi
        .getCustomerShowAttendance(newGridDataState)
        .then((response) => {
          setShowAttendance(response.value);
          setTotal(response['@odata.count']);
        })
        .catch(() => {
          setShowAttendance([]);
        });
    } else {
      setShowAttendance([]);
    }
  };

  const handleGridDataStateChange = (e: any) => {
    setGridDataState(e.dataState);
    loadShowAttendance(e.dataState);
  };

  const onFilterCollectionChange: (value: number) => void = (value) => {
    setSelectedShow(value);
  };

  useEffect(() => {
    const filterCollectionFilters = selectedShow ? activeShows[selectedShow].value : null;
    const filters: (CompositeFilterDescriptor | FilterDescriptor)[] = [];

    if (filterCollectionFilters) {
      filters.push(filterCollectionFilters);
    } else {
      filters.push(activeShows[0]?.value);
    }

    if (search.get('allcustomers') !== 'true') filters.push(defaultFilter);

    const newDataState: ODataState = {
      ...gridDataState,
      filter: {
        filters: filters,
        logic: 'and',
      },
      skip: 0,
      take: pageSize,
      sort: [{ field: 'customerName', dir: 'asc' }],
    };

    setGridDataState(newDataState);
    loadShowAttendance(newDataState);
  }, [activeShows, selectedShow, search]);

  useEffect(() => {
    loadActiveShows();
  }, []);

  const showOrderCell = (cellProps: GridCellProps) => <ShowOrderCell {...cellProps} />;

  const handlePageChange = (e) => {
    const newPageSize = e.page.take;
    setGridDataState((prevState) => ({
      ...prevState,
      skip: e.page.skip,
      take: newPageSize,
    }));
    savePagingSettings(newPageSize, CustomerShowPageSizeKey);
  };

  return (
    <div className="progress-table-container">
      {isLoading && <CircularProgress></CircularProgress>}
      <Card>
        <CardHeader
          title={
            <TableHeader>
              <DropdownLink
                options={activeShows}
                startIcon={<ListIcon sx={{ fontSize: '2px' }} />}
                defaultText={activeShows.length > 0 ? activeShows[0].text : 'Select Show'}
                onChange={onFilterCollectionChange}
              />
            </TableHeader>
          }
        />
        <Box
          sx={{
            minWidth: 500,
          }}
        ></Box>
        <Grid
          data={showAttendance}
          {...gridDataState}
          pageable={{
            buttonCount: 4,
            pageSizes: [20, 50, 100, 500],
            pageSizeValue: pageSize,
          }}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          total={total}
          dataItemKey="customerNumber;showId"
          sortable
          filterOperators={FilterOperators}
          columnMenu={ColumnMenu}
          resizable={true}
          scrollable="scrollable"
          onDataStateChange={handleGridDataStateChange}
        >
          <GridColumn field="customerName" title="Customer Name" width={260} />
          <GridColumn field="customerNumber" title="Customer Number" width={140} />
          <GridColumn field="numberOfAttendees" title="Number Of Attendees" width={180} />
          <GridColumn field="finishedOrderTotal" title="Order Total" format="{0:c}" width={100} />
          <GridColumn field="unFinishedOrderTotal" cells={{ data: showOrderCell }} title="Unfinished Order Total" format="c2" width={180} />
        </Grid>
      </Card>
    </div>
  );
};
export default CustomerShows;
